import { Button } from '@heidi/design-system/button';
import { FormattedMessage } from 'react-intl';

export interface SourceSearchMultiSelectControlsProps {
  /** True when every visible row is already in the pending set —
   *  toggles the CTA from "Select all (N)" to "Deselect all". */
  allVisibleSelected: boolean;
  /** True when bulk-adding all visible not-yet-pending rows would fit
   *  within the per-collection cap. When false (and the user isn't
   *  already in Deselect mode) we hide the CTA entirely so the user
   *  doesn't see a "Select all (200)" that can only take 3. They can
   *  still tick rows individually — the cap counter above explains
   *  the limit. */
  canSelectAllVisible: boolean;
  /** Number of visible results — drives the "(N)" suffix on the
   *  Select all CTA. */
  visibleCount: number;
  /** Number of currently pending selections — gates whether the
   *  Clear affordance renders. */
  pendingCount: number;
  onSelectAll: () => void;
  onClear: () => void;
}

/**
 * Right-slot affordances rendered inside `CustomDomainRow` for the
 * multi-select dropdown — the "Select all (N)" / "Deselect all" CTA
 * and the "Clear" link that drops pending selections.
 *
 * Extracted from `SourceSearchInput` so the layout file stays under
 * the codebase-wide `max-lines: 300` cap and so the cap-aware
 * visibility rules for "Select all" live alongside the JSX they
 * actually gate.
 */
export const SourceSearchMultiSelectControls = ({
  allVisibleSelected,
  canSelectAllVisible,
  visibleCount,
  pendingCount,
  onSelectAll,
  onClear,
}: SourceSearchMultiSelectControlsProps) => (
  <>
    {(allVisibleSelected || canSelectAllVisible) && (
      <Button
        type="button"
        variant="link"
        size="xxs"
        onMouseDown={(e) => {
          e.preventDefault();
          onSelectAll();
        }}
        data-testid="source-search-select-all"
      >
        {allVisibleSelected ? (
          <FormattedMessage
            defaultMessage="Deselect all"
            id="C1GfoQIMQP"
            description="Toggles off the selection of all visible sources in the multi-select dropdown"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Select all ({count})"
            id="5OzmDPc86H"
            description="Selects every visible source in the multi-select dropdown; count is the number of currently visible results"
            values={{ count: visibleCount }}
          />
        )}
      </Button>
    )}
    {pendingCount > 0 && (
      <Button
        type="button"
        variant="link"
        size="xxs"
        onMouseDown={(e) => {
          e.preventDefault();
          onClear();
        }}
      >
        <FormattedMessage
          defaultMessage="Clear"
          id="UYa8F7IAqS"
          description="Clears all pending source selections in the multi-select dropdown"
        />
      </Button>
    )}
  </>
);
